import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus,faTools,faCheckCircle, faTrashAlt, faCalculator, faEdit } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons
import { StandardURL } from "../App";


const UpdateVacatingPage = () => {
  const navigate = useNavigate();
  const [repairItems, setRepairItems] = useState([{ item: "", price: "" }]);
  const [totalAmount, setTotalAmount] = useState("");

  // Function to calculate total amount
  const calculateTotalAmount = () => {
    let total = 0;
    repairItems.forEach((item) => {
      if (item.price) {
        total += parseFloat(item.price);
      }
    });
    setTotalAmount(total.toFixed(2));
  };

  useEffect(() => {
    calculateTotalAmount(); // Calculate total amount initially
  }, [repairItems]); // Recalculate total amount whenever repairItems change

  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    const list = [...repairItems];
    list[index][name] = value;
    setRepairItems(list);
  };

  const handleAddItem = () => {
    setRepairItems([...repairItems, { item: "", price: "" }]);
  };

  const handleRemoveItem = (index) => {
    const list = [...repairItems];
    list.splice(index, 1);
    setRepairItems(list);
  };

  const handleSubmit = () => {
    // Send patch request to update vacating house data
    const vacatingId = localStorage.getItem("selectedVacatingId");
    const token = localStorage.getItem("token");
    const requestData = {
      repaircharges: repairItems
        .map((item) => `${item.item} ${item.price}`)
        .join(", "), // Concatenate repair charges
      totalamountrepairs: totalAmount, // Use total amount calculated dynamically
    };

    axios
      .patch(
        `${StandardURL}/iNyumba/vacatinghouse/${vacatingId}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        navigate(-1);
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error updating vacating house data:", error);
      });
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                <FontAwesomeIcon icon={faEdit} className="mr-4 text-purple-600" />
                Update Vacating House
              </h1>
            </div>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-center">
              Add or modify repair items and their costs for the vacating house. The total amount will be calculated automatically.
            </p>

            {/* --- Repair Items Section --- */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-md mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4 flex items-center">
                <FontAwesomeIcon icon={faTools} className="mr-3 text-gray-600" />
                Repair Items & Quotations
              </h2>

              {repairItems.length === 0 && (
                <div className="text-center text-gray-500 italic py-4">
                  No repair items added yet. Click "Add Item" to start.
                </div>
              )}

              <div className="space-y-4 mb-6">
                {repairItems.map((item, index) => (
                  <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-center bg-white p-4 rounded-md shadow-sm border border-gray-200">
                    <input
                      type="text"
                      name="item"
                      value={item.item}
                      onChange={(event) => handleInputChange(index, event)}
                      placeholder="Item destroyed (e.g., Broken Window)"
                      className="flex-grow w-full sm:w-1/2 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400 mb-2 sm:mb-0 sm:mr-3"
                      required
                    />
                    <input
                      type="number"
                      name="price"
                      value={item.price}
                      onChange={(event) => handleInputChange(index, event)}
                      placeholder="Price (Ksh)"
                      className="w-full sm:w-1/4 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400 mb-2 sm:mb-0 sm:mr-3"
                      min="0" // Ensure non-negative price
                      required
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md shadow-sm transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      title="Remove item"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} className="mr-2 sm:mr-0 sm:text-lg" />
                      <span className="sm:hidden">Remove</span>
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-start">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300 ease-in-out transform hover:-translate-y-0.5"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add Item
                </button>
              </div>
            </div>

            {/* --- Total Amount Section --- */}
            <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 flex items-center justify-between shadow-inner">
              <label htmlFor="total-amount" className="text-xl font-bold flex items-center">
                <FontAwesomeIcon icon={faCalculator} className="mr-3 text-blue-600" />
                Total Estimated Repair Cost:
              </label>
              <input
                id="total-amount"
                type="text"
                value={`Ksh ${parseFloat(totalAmount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                readOnly
                className="bg-transparent border-none text-right text-3xl font-extrabold text-blue-800 w-auto p-0 focus:outline-none"
              />
            </div>

            {/* --- Submit Button --- */}
            <div className="flex justify-end">
              <button
                type="submit"
                onClick={handleSubmit}
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-md shadow-xl text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300 ease-in-out transform hover:scale-105"
              >
                Save Updates
                <FontAwesomeIcon icon={faCheckCircle} className="ml-3" />
              </button>
            </div>
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default UpdateVacatingPage;
